import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { DealRow } from "../pipeline/boardData";
import type { PlayCo } from "../session/playbook";

export type DirFull = {
  id: number;
  source: string;
  estado: string;
  razon_social: string | null;
  nombre_comercial: string | null;
  sector: string | null;
  tamano: string | null;
  rango_personal: string | null;
  emp_min: number | null;
  telefono: string | null;
  email: string | null;
  email_tipo: string | null;
  sitio_web: string | null;
  direccion: string | null;
  municipio: string | null;
  cp: string | null;
  scian: string | null;
  actividad: string | null;
  alta_denue: string | null;
  fits: string[];
  priority: number;
  tier: string | null;
  company_id: number | null;
  user_edited_fields: string[];
  researched_at: string | null;
  dnc: boolean;
  denue_id: string | null;
  denue_status: "activa" | "cerrada";
  denue_closed_at: string | null;
  thermo_base: number | null;
};

export type CompanyRow = {
  id: number;
  name: string;
  sector: string | null;
  phone_number: string | null;
  website: string | null;
  address: string | null;
  zipcode: string | null;
  city: string | null;
  state_abbr: string | null;
  description: string | null;
  sales_id: number | null;
};

export type ContactRow = {
  id: number;
  first_name: string | null;
  last_name: string | null;
  title: string | null;
  phone_jsonb: { number?: string }[] | null;
  email_jsonb: { email?: string }[] | null;
  preferred_channel: string | null;
};

export type Ficha = {
  d: DirFull | null;
  co: CompanyRow | null;
  companyId: number | null;
  name: string;
  contacts: ContactRow[];
  deals: DealRow[];
  dnc: boolean;
  pc: PlayCo;
  phone: string | null;
  email: string | null;
};

export const contactPhone = (c?: ContactRow) =>
  c?.phone_jsonb?.[0]?.number ?? null;
export const contactEmail = (c?: ContactRow) =>
  c?.email_jsonb?.[0]?.email ?? null;

/**
 * Datos de la ficha 360 (PRD D.3): la fila del directorio y, si ya pasó al CRM,
 * la empresa, sus contactos y oportunidades. Sirve para /directory/:id y /companies/:id.
 */
export const useFicha = (key: { dirId?: number; companyId?: number }) => {
  const qc = useQueryClient();
  const base = useQuery({
    queryKey: ["ficha", key.dirId, key.companyId],
    queryFn: async () => {
      const sb = getSupabaseClient();
      const q = sb.from("directory").select("*");
      const { data: d } = key.dirId
        ? await q.eq("id", key.dirId).maybeSingle()
        : await q.eq("company_id", key.companyId!).limit(1).maybeSingle();
      const companyId: number | null = key.companyId ?? d?.company_id ?? null;
      if (!companyId)
        return { d: d as DirFull | null, co: null, contacts: [], companyId };
      const [{ data: co }, { data: contacts }] = await Promise.all([
        sb.from("companies").select("*").eq("id", companyId).maybeSingle(),
        sb
          .from("contacts")
          .select(
            "id, first_name, last_name, title, phone_jsonb, email_jsonb, preferred_channel",
          )
          .eq("company_id", companyId)
          .order("id"),
      ]);
      return {
        d: d as DirFull | null,
        co: co as CompanyRow | null,
        contacts: (contacts ?? []) as ContactRow[],
        companyId,
      };
    },
  });
  const companyId = base.data?.companyId ?? null;
  const deals = useQuery({
    queryKey: ["deal_board", "company", companyId],
    enabled: !!companyId,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("deal_board")
        .select("*")
        .eq("company_id", companyId!)
        .order("id");
      if (error) throw error;
      return data as DealRow[];
    },
  });

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["ficha"] });
    [
      "deal_board",
      "company_board",
      "ficha_acts",
      "ficha_tasks",
      "my_day",
      "funnel_by_offer",
      "directory_thermo",
      "thermo_alerts",
    ].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  };

  if (!base.data) return { ficha: null, isPending: base.isPending, refetch };
  const { d, co, contacts } = base.data;
  if (!d && !co) return { ficha: null, isPending: false, refetch };
  const name =
    d?.nombre_comercial ?? d?.razon_social ?? co?.name ?? "Sin nombre";
  const ficha: Ficha = {
    d,
    co,
    companyId,
    name,
    contacts,
    deals: deals.data ?? [],
    dnc: !!d?.dnc,
    pc: {
      nombre: name,
      municipio: d?.municipio ?? co?.city ?? "su zona",
      actividad: d?.actividad ?? co?.description ?? co?.sector ?? "su giro",
      rango: d?.rango_personal?.replace(" personas", "") ?? "varias",
      sector: d?.sector ?? co?.sector ?? "",
      sitio: d?.sitio_web ?? co?.website ?? null,
    },
    phone: contactPhone(contacts[0]) ?? d?.telefono ?? co?.phone_number ?? null,
    email: contactEmail(contacts[0]) ?? d?.email ?? null,
  };
  return { ficha, isPending: false, refetch };
};
